import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import { validateCustomer } from '../../../utils/validationUtils';
import { db } from '../../../firebase';
import {
  fetchEmployeesByBusiness,
  addEmployee as addEmployeeService,
  updateEmployee as updateEmployeeService,
  softDeleteEmployee,
  fetchActiveServices,
} from '../services/employeeService';
import useEmployeesStore from '../store/employeesStore';

/**
 * Çalışan listesi ve CRUD operasyonlarını yönetir.
 *
 * Server state (employees, loading) burada yaşar.
 * UI state (searchTerm, modal, form) useEmployeesStore'dadır.
 */
export function useEmployees() {
  const { user }           = useAuth();
  const { t }              = useTranslation('services');
  const businessId         = user?.businessId;

  // Store'dan seçici ile okuma — yalnızca ilgili dilime abone ol
  const searchTerm  = useEmployeesStore((s) => s.searchTerm);
  const isEditing   = useEmployeesStore((s) => s.isEditing);
  const editingId   = useEmployeesStore((s) => s.editingId);
  const formData    = useEmployeesStore((s) => s.formData);
  const closeModal  = useEmployeesStore((s) => s.closeModal);

  const [employees, setEmployees] = useState([]);
  const [services,  setServices]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);

  // ── Veri çekme ─────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [emps, svcs] = await Promise.all([
        fetchEmployeesByBusiness(businessId),
        fetchActiveServices(businessId),
      ]);
      setEmployees(emps);
      setServices(svcs);
    } catch (err) {
      console.error('[useEmployees] load:', err);
      toast.error('Çalışanlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  // ── Client-side filtreleme (Firestore sorgusu tetiklenmez) ──
  const filteredEmployees = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return employees;
    return employees.filter((e) =>
      e.name.toLowerCase().includes(term) ||
      e.emailAddress.toLowerCase().includes(term) ||
      (e.phone?.number || '').includes(term)
    );
  }, [employees, searchTerm]);

  // ── Kaydetme (ekle veya güncelle) ──────────────────────────
  const save = useCallback(async () => {
    if (!businessId) return false;
    setSaving(true);
    try {
      const validation = await validateCustomer(formData, db, editingId, businessId);
      if (!validation.isValid) {
        Object.values(validation.errors).forEach((msg) => toast.error(msg));
        return false;
      }
      if (isEditing) {
        await updateEmployeeService(editingId, formData);
        toast.success(t('employee.updatedAlert'));
      } else {
        await addEmployeeService(formData, businessId);
        toast.success(t('employee.addedAlert'));
      }
      await load();
      closeModal();
      return true;
    } catch (err) {
      console.error('[useEmployees] save:', err);
      toast.error(`${t('employee.errorAlert')}: ${err.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [businessId, formData, isEditing, editingId, closeModal, load, t]);

  // ── Soft delete ────────────────────────────────────────────
  const remove = useCallback(async (employeeId) => {
    try {
      await softDeleteEmployee(employeeId);
      toast.success(t('employee.deletedAlert'));
      await load();
    } catch (err) {
      console.error('[useEmployees] remove:', err);
      toast.error('Çalışan silinemedi.');
    }
  }, [load, t]);

  return { filteredEmployees, services, loading, saving, save, remove };
}
